import { assignInlineVars } from "@vanilla-extract/dynamic";
import React from "react";
import GenericStickyModal from "web/react/components/generic-sticky-modal/generic-sticky-modal";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Heading } from "web/react/emo/heading";
import { PriceComparisonTable } from "web/react/emo/product-comparison-table";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useWindowHeight } from "web/react/hooks/use-window-height/use-window-height";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import history from "web/script/utils/history";
import * as styles from "./product-comparison.css";

const ID = "compare-price-section";

export function ComparePriceLink({ productCount }: { productCount: number }): JSX.Element {
    const { isTabletViewport, isDesktopViewport } = useDomViewport();
    const {
        comparison: { openCompareModal },
    } = useInStockProductPageContext();

    function scrollToComparePrice(): void {
        document.getElementById(ID)?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
        });

        const url = window.location.pathname + `#${ID}`;

        history.pushState(new URL(window.location.origin + url), {
            saveScrollPosition: false,
            bypassRouting: true,
        });
    }

    function onClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void {
        event.preventDefault();

        if (isTabletViewport || isDesktopViewport) {
            scrollToComparePrice();
        } else {
            openCompareModal();
        }

        analytics.event("compare-prices", "clicked", "pdp");
    }

    return (
        <button className={styles.comparePricesButton}>
            <a href={`#${ID}`} className={styles.flex} onClick={onClick}>
                <Text as="span" textStyle={"callout"} className={styles.comparePriceCTA}>
                    {gettext("product.buy_area.compare_count_prices.cta", {
                        count: productCount,
                    })}
                </Text>
                {(isTabletViewport || isDesktopViewport) && (
                    <SVGIcon name="arrow-down" className={styles.compareSVGIcon} />
                )}
            </a>
        </button>
    );
}

export default function ProductPriceComparison(): JSX.Element | null {
    const { isTabletViewport, isDesktopViewport } = useDomViewport();
    const {
        buyOptions,
        sortedBuyOptionsKeys,
        termsUrl,
        comparison: { isComparePriceOpen, closeCompareModal },
    } = useInStockProductPageContext();

    const pageHeight = useWindowHeight();

    if (!sortedBuyOptionsKeys || sortedBuyOptionsKeys.length === 1) return null;

    const priceCompareTitle = (
        <Heading as={"h2"} textStyle={"title-2"} className={styles.title}>
            {
                // "Compare all prices"
                gettext("in_stock_product.compare_prices.compare_prices_title")
            }
        </Heading>
    );

    const priceComparisonArea = (
        <VStack spacing="md">
            <Text as="span" textStyle={"body-1"} color={"secondary"}>
                {
                    // "The best prices we've found across {store_count} stores."
                    gettext("in_stock_product.compare_prices.compare_prices_desc")
                }
            </Text>
            <PriceComparisonTable
                buyOptions={buyOptions}
                sortedBuyOptionsKeys={sortedBuyOptionsKeys}
                termsUrl={termsUrl}
            />
        </VStack>
    );

    if (isTabletViewport || isDesktopViewport) {
        return (
            <section id={ID}>
                <View>
                    {priceCompareTitle}
                    {priceComparisonArea}
                </View>
            </section>
        );
    }

    return (
        <GenericStickyModal
            isOpen={isComparePriceOpen}
            onAfterOpen={() => {
                analytics.event("compare-prices", "shown", "pdp");
            }}
            onClose={() => {
                closeCompareModal();
                analytics.event("compare-prices", "modal_dismissed", "pdp");
            }}
            className={styles.comparisonModal}
            closeClassName={styles.closeButton}
            noScroll
        >
            <div
                style={assignInlineVars({
                    [styles.pageHeight]: pageHeight ? `${pageHeight}px` : "100vh",
                })}
                className={styles.mobileWrapper}
            >
                <View paddingX="sm">{priceCompareTitle}</View>
                <div className={styles.mobileTable}>{priceComparisonArea}</div>
            </div>
        </GenericStickyModal>
    );
}
